import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import * as yup from 'yup';

import { Project } from '@gameonly/core';
import { useFormik } from 'formik';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useUser from '../authentication/context/useUser';
import { ReportInfo } from '../project/CreateReport';
import { getParameterFromId } from '../project/utils';
import TextBlock from '../results/TextBlock';
import Button, { WhiteButton } from '../ui/Button';

interface CreateReportModalProps {
  onClose: () => void;
  onNext: (values: ReportInfo) => void;
  project?: Project;
  validationButtonLabel: string;
}

const validationSchema = yup.object({
  name: yup.string().required('Champ obligatoire'),
  type: yup.string().required('Type de rapport obligatoire'),
  studio: yup.string().required('Studio obligatoire'),
});

const projectReportCorrespondance = {
  Prévisionnel: 'forecast',
  Définitif: 'realized',
};

const CreateReportModal = ({
  onClose,
  onNext,
  project,
  validationButtonLabel
}: CreateReportModalProps) => {
  const { t } = useTranslation(['cnc-reports', 'common', 'model']);
  const called = useRef(false);
  const autofocusFirst = useRef(true);

  const { user, changeProfile } = useUser();
  const [showUpdateProfile, setShowUpdateProfile] = useState(false);

  const typeParameter = project
    ? getParameterFromId(project?.sectors, 'previsionnel_posteriori_v2')
    : undefined;
  const typeParameterValue =
    projectReportCorrespondance?.[
      typeParameter?.value as keyof typeof projectReportCorrespondance
    ] ?? '';

  const formik = useFormik({
    initialValues: {
      name: '',
      type: typeParameterValue,
      studio: user?.company || '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (user?.company !== values.studio) {
        setShowUpdateProfile(true);
        return;
      }
      handleNext(values)
    },
    enableReinitialize: true,
  });

  const handleNext = async (values: ReportInfo) => {
    onNext(values);
  };

  const handleClose = () => {
    called.current = false;
    autofocusFirst.current = true;
    onClose && onClose();
  };

  if (showUpdateProfile) {
    return (
      <Dialog
        open={showUpdateProfile}
        onClose={() => setShowUpdateProfile(false)}
      >
        <DialogTitle>{t('common:update-profile')}</DialogTitle>
        <DialogContent>
          <TextBlock
            result={{
              type: 'text',
              text: t('cnc-reports:creation-modal.update-profile-info', {
                actualStudio: user?.company,
                newStudio: formik.values.studio,
              }),
            }}
            translate={false}
          ></TextBlock>
        </DialogContent>
        <DialogActions>
          <WhiteButton
            onClick={() => {
              setShowUpdateProfile(false);
              handleNext(formik.values);
            }}
          >
            {t('common:no')}
          </WhiteButton>
          <Button
            onClick={() => {
              setShowUpdateProfile(false);
              changeProfile({
                email: user?.email || '',
                firstName: user?.firstName || '',
                lastName: user?.lastName || '',
                country: user?.country || '',
                region: user?.region || '',
                company: formik.values.studio,
                companyType: user?.companyType || '',
                news: user?.news || false,
              });
              handleNext(formik.values);
            }}
          >
            {t('common:yes')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <DialogContentText
        id="scroll-dialog-description"
        tabIndex={-1}
        component="div"
      >
        <TextBlock
          className="small"
          result={{
            type: 'text',
            text: t('cnc-reports:creation-modal.explanation'),
          }}
          translate={false}
        ></TextBlock>
        <TextField
          id="name"
          name="name"
          label={t('cnc-reports:creation-modal.name')}
          autoFocus
          value={formik.values.name}
          onChange={formik.handleChange}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          fullWidth
        />
        <FormControl
          component="fieldset"
          margin="normal"
          error={formik.touched.type && Boolean(formik.errors.type)}
        >
          <FormLabel component="legend">
            {t('cnc-reports:creation-modal.report-type')}
          </FormLabel>
          <RadioGroup
            id="type"
            name="type"
            value={formik.values.type}
            onChange={formik.handleChange}
          >
            <FormControlLabel
              value="forecast"
              control={<Radio />}
              label={t('cnc-reports:creation-modal.type-forecast')}
            />
            <FormControlLabel
              value="realized"
              control={<Radio />}
              label={t('cnc-reports:creation-modal.type-realized')}
            />
          </RadioGroup>
          {formik.touched.type && formik.errors.type && (
            <div style={{ color: 'red', marginTop: '0.5em' }}>
              {formik.errors.type}
            </div>
          )}
        </FormControl>
        <TextField
          id="studio"
          name="studio"
          label={t('cnc-reports:creation-modal.studio')}
          value={formik.values.studio}
          onChange={formik.handleChange}
          error={formik.touched.studio && Boolean(formik.errors.studio)}
          helperText={formik.touched.studio && formik.errors.studio}
          fullWidth
        />
      </DialogContentText>
      <DialogActions
        sx={{ position: 'sticky', bottom: 0, background: 'white' }}
      >
        <WhiteButton onClick={handleClose}>{t('common:cancel')}</WhiteButton>
        <Button type="submit">
          {t(validationButtonLabel)}
        </Button>
      </DialogActions>
    </form>
  );
};

export default CreateReportModal;
